import { ModelCardInterface, TokenUsageInterface } from "@dashart/dashart-gpt-shared-library";

/**
 * Pricing for chat models is provided per 1M tokens,
 * e.g. costPerInputToken: 0.005 means $0.005 per 1M input tokens.
 */
export const PRICE_BASE = 1000000;

export interface TokenUsageCostSummaryInterface {
    total: number;
    input: number;
    output: number;
    reasoning: number;
    other: number;
    totalCost: number;
    inputCost: number;
    outputCost: number;
    reasoningCost: number;
}

/**
 * Looks up the price per token for a given usage entry.
 * Returns undefined when no pricing is available for the model or token type
 * (unknown models / ocr / image have no per-token price in model cards).
 */
export const getPricePerToken = (model: ModelCardInterface | undefined, tokenType: string): number | undefined => {
    if (!model) {
        return undefined;
    }
    switch (tokenType) {
        case 'input':
            return model.costPerInputToken ?? 0;
        case 'output':
            return model.costPerOutputToken ?? 0;
        case 'reasoning':
            // Reasoning tokens are billed at the output rate
            return model.costPerOutputToken ?? 0;
        default:
            return undefined;
    }
};

/**
 * Calculates the cost in USD for a single token usage entry.
 * Returns undefined when no pricing is available.
 */
export const calculateTokenCost = (entry: TokenUsageInterface, model: ModelCardInterface | undefined): number | undefined => {
    const pricePerToken = getPricePerToken(model, entry.tokenType);
    if (pricePerToken === undefined) {
        return undefined;
    }
    return (entry.tokenCount * pricePerToken) / PRICE_BASE;
};

/**
 * Strips an optional provider prefix from a model id,
 * e.g. "anthropic/claude-opus-5" -> "claude-opus-5".
 */
export const stripModelProviderPrefix = (modelId: string): string => {
    return modelId.replace(/^[a-z0-9][a-z0-9.-]*\//i, '');
};

/**
 * Normalizes a model id into a canonical key that can be used to resolve
 * pricing. Handles the common id-churn between usage records and model cards:
 * - lowercasing / trimming
 * - provider prefix ("anthropic/claude-opus-5" -> "claude-opus-5")
 * - snapshot/version suffixes ("gpt-5-mini-2025-08-07" -> "gpt-5-mini",
 *   "gpt-4-0613" -> "gpt-4", "deepseek-r1:free" -> "deepseek-r1")
 * - version separator style ("anthropic/claude-haiku-4.5" -> "claude-haiku-4-5")
 */
export const normalizeModelId = (modelId: string): string => {
    let id = (modelId ?? '').trim().toLowerCase();
    id = stripModelProviderPrefix(id);
    id = id
        .replace(
            /(?:-(?:19|20)\d{2}(?:[.-]?\d{1,2})?(?:[.-]?\d{1,2})?|-[0-9]{4,8}|:[a-z0-9][a-z0-9-]*|@[a-z0-9][a-z0-9.-]*)$/,
            ''
        )
        .replace(/[-_\s]+$/, '')
        // unify version separators: "4.5" vs "4-5" -> "4-5"
        .replace(/(\d)\.(\d)/g, '$1-$2');
    return id;
};

/**
 * Builds a lookup map for model cards by id.
 * Keys the model by its full id, its short (provider-stripped) id and its
 * normalized id, so usage records in any of those formats resolve.
 * Existing keys are never overwritten: the first card wins, keeping
 * exact matches and short-id matches authoritative over normalized matches.
 */
export const buildModelCardLookup = (modelGroups: { [provider: string]: ModelCardInterface[] }): Record<string, ModelCardInterface> => {
    const lookup: Record<string, ModelCardInterface> = {};
    Object.values(modelGroups).flat().forEach((model) => {
        lookup[model.id] = model;
        const shortId = model.id.split('/').pop();
        if (shortId && !lookup[shortId]) {
            lookup[shortId] = model;
        }
        const normalizedId = normalizeModelId(model.id);
        if (normalizedId && !lookup[normalizedId]) {
            lookup[normalizedId] = model;
        }
    });
    return lookup;
};

/**
 * Resolves the model card for a usage record's model id, tolerating format
 * differences: exact id -> short (provider-stripped) id -> normalized id.
 */
export const resolveModelCard = (
    modelCards: Record<string, ModelCardInterface>,
    modelId: string | undefined | null
): ModelCardInterface | undefined => {
    if (!modelId) {
        return undefined;
    }
    const shortId = modelId.split('/').pop();
    return modelCards[modelId]
        ?? (shortId ? modelCards[shortId] : undefined)
        ?? modelCards[normalizeModelId(modelId)];
};

/**
 * Summarizes token counts and costs over a list of usage entries.
 */
export const summarizeTokenUsage = (
    data: TokenUsageInterface[],
    modelCards: Record<string, ModelCardInterface>
): TokenUsageCostSummaryInterface => {
    const summary: TokenUsageCostSummaryInterface = {
        total: 0,
        input: 0,
        output: 0,
        reasoning: 0,
        other: 0,
        totalCost: 0,
        inputCost: 0,
        outputCost: 0,
        reasoningCost: 0,
    };

    data.forEach((entry) => {
        const cost = calculateTokenCost(entry, resolveModelCard(modelCards, entry.modelId)) ?? 0;
        summary.total += entry.tokenCount;
        summary.totalCost += cost;

        switch (entry.tokenType) {
            case 'input':
                summary.input += entry.tokenCount;
                summary.inputCost += cost;
                break;
            case 'output':
                summary.output += entry.tokenCount;
                summary.outputCost += cost;
                break;
            case 'reasoning':
                summary.reasoning += entry.tokenCount;
                summary.reasoningCost += cost;
                break;
            default:
                summary.other += entry.tokenCount;
                break;
        }
    });

    return summary;
};

/**
 * Formats a cost as USD currency, keeping small per-request amounts visible.
 * The fraction digits can be raised for per-entry rows where single
 * requests are much smaller than a summary. Returns an empty string when
 * no pricing is available.
 */
export const formatCost = (cost: number | undefined, fractionDigits: number = 3): string => {
    if (cost === undefined) {
        return '';
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: fractionDigits,
    }).format(cost);
};
